import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie } from 'lucide-react';
import { LandingLayout } from '../landing/shared/LandingLayout';
import { SEOHead } from '../landing/shared/SEOHead';

const CookieSettingsButton = lazy(() =>
  import('@/components/CookieConsentBanner')
    .then(m => ({ default: m.CookieSettingsButton }))
    .catch(() => ({ default: () => null }))
);

export default function CookiePolicyPage() {
  const { t } = useTranslation('legal');

  return (
    <LandingLayout>
      <SEOHead
        title={t('cookiePolicy.title', 'Cookie Policy')}
        description={t('cookiePolicy.seoDescription')}
        canonical="https://slotyapp.com/cookies"
      />
      <div className="bg-gray-50 py-12">
        {/* Page Header */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="flex items-center gap-3">
            <Cookie className="h-8 w-8 text-amber-500" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('cookiePolicy.title', 'Cookie Policy')}
              </h1>
              <p className="text-sm text-gray-500">
                {t('cookiePolicy.lastUpdated', 'Last updated')}: December 2024
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border p-6 sm:p-8 space-y-8">
            {/* What Are Cookies */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('cookiePolicy.sections.intro.title')}
              </h2>
              <p className="text-gray-600">
                {t('cookiePolicy.sections.intro.content')}
              </p>
            </section>

            {/* How We Use Cookies */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('cookiePolicy.sections.howWeUse.title')}
              </h2>
              <p className="text-gray-600">
                {t('cookiePolicy.sections.howWeUse.content')}
              </p>
            </section>

            {/* Types of Cookies */}
            <section className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('cookiePolicy.sections.types.title')}
              </h2>

              {/* Necessary Cookies */}
              <div className="pl-4 border-l-4 border-green-500 bg-green-50 p-4 rounded-r-lg">
                <h3 className="font-medium text-gray-900">
                  {t('cookiePolicy.sections.types.necessary.title')}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {t('cookiePolicy.sections.types.necessary.description')}
                </p>
                <ul className="mt-2 space-y-1 text-sm text-gray-600 list-disc list-inside">
                  {(t('cookiePolicy.sections.types.necessary.items', { returnObjects: true }) as string[]).map(
                    (item: string, idx: number) => (
                      <li key={idx}>{item}</li>
                    )
                  )}
                </ul>
              </div>

              {/* Functional Cookies */}
              <div className="pl-4 border-l-4 border-blue-500 bg-blue-50 p-4 rounded-r-lg">
                <h3 className="font-medium text-gray-900">
                  {t('cookiePolicy.sections.types.functional.title')}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {t('cookiePolicy.sections.types.functional.description')}
                </p>
                <ul className="mt-2 space-y-1 text-sm text-gray-600 list-disc list-inside">
                  {(t('cookiePolicy.sections.types.functional.items', { returnObjects: true }) as string[]).map(
                    (item: string, idx: number) => (
                      <li key={idx}>{item}</li>
                    )
                  )}
                </ul>
              </div>

              {/* Analytics Cookies */}
              <div className="pl-4 border-l-4 border-purple-500 bg-purple-50 p-4 rounded-r-lg">
                <h3 className="font-medium text-gray-900">
                  {t('cookiePolicy.sections.types.analytics.title')}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {t('cookiePolicy.sections.types.analytics.description')}
                </p>
                <ul className="mt-2 space-y-1 text-sm text-gray-600 list-disc list-inside">
                  {(t('cookiePolicy.sections.types.analytics.items', { returnObjects: true }) as string[]).map(
                    (item: string, idx: number) => (
                      <li key={idx}>{item}</li>
                    )
                  )}
                </ul>
              </div>
            </section>

            {/* Third-Party Cookies */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('cookiePolicy.sections.thirdParty.title')}
              </h2>
              <p className="text-gray-600">
                {t('cookiePolicy.sections.thirdParty.content')}
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="font-medium">Stripe:</span>
                  <span>{t('cookiePolicy.sections.thirdParty.stripe')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium">Google:</span>
                  <span>{t('cookiePolicy.sections.thirdParty.google')}</span>
                </li>
              </ul>
            </section>

            {/* Managing Cookies */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('cookiePolicy.sections.manage.title')}
              </h2>
              <p className="text-gray-600">
                {t('cookiePolicy.sections.manage.content')}
              </p>
              <div className="mt-4">
                <Suspense fallback={null}><CookieSettingsButton /></Suspense>
              </div>
            </section>

            {/* Browser Settings */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('cookiePolicy.sections.browserSettings.title')}
              </h2>
              <p className="text-gray-600">
                {t('cookiePolicy.sections.browserSettings.content')}
              </p>
              <ul className="space-y-1 text-gray-600 list-disc list-inside">
                {(t('cookiePolicy.sections.browserSettings.items', { returnObjects: true }) as string[]).map(
                  (item: string, idx: number) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
            </section>

            {/* Updates */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('cookiePolicy.sections.updates.title')}
              </h2>
              <p className="text-gray-600">
                {t('cookiePolicy.sections.updates.content')}
              </p>
            </section>

            {/* Contact */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('cookiePolicy.sections.contact.title')}
              </h2>
              <p className="text-gray-600">
                {t('cookiePolicy.sections.contact.content')}
              </p>
              <p className="text-blue-600">
                <a href={`mailto:${t('cookiePolicy.sections.contact.email')}`}>
                  {t('cookiePolicy.sections.contact.email')}
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </LandingLayout>
  );
}
